import Hls from 'hls.js';

import { AppDispatch } from 'containers/Femtasy';

import { HlsErrorHandler, hlsErrorHandler } from 'helpers/player';

import { AlgoliaIndex } from 'hooks/useAlgolia';

import { playbackFailedThunk } from 'store/modules/player';

import { ContentType } from 'types/contentType';

let hls: Hls;
let hlsErrorHandlerInstance: HlsErrorHandler | undefined;
// Set to the audioUrl the circuit breaker gave up on, so an in-session
// re-render that calls handleCurrentlyPlaying again with the same
// still-failing track (the give-up dispatch itself changes player Redux
// state and can trigger one) doesn't rebuild Hls with a fresh retry budget
// and silently resume retrying. Cleared as soon as a different track starts
// loading, so reselecting the failed track later still gets a fresh budget -
// this only blocks an immediate retry of the exact track that just gave up.
// This is plain in-memory module state, not persisted anywhere, so a real
// page reload (or navigating away and back) always starts with a clean
// budget regardless of this guard.
let gaveUpOnAudioUrl: string | undefined;

// Cancels the previous track's pending retry/settle timers (if any) before
// tearing down its Hls instance, so a timer scheduled for an abandoned track
// can't later call startLoad()/recoverMediaError() on it or emit a stray
// "recovered" metric for a track the user already left.
const destroyHls = () => {
  hlsErrorHandlerInstance?.cleanup();
  hlsErrorHandlerInstance = undefined;
  hls.destroy();
};

// MEDIA_ERR_NETWORK (https://developer.mozilla.org/en-US/docs/Web/API/MediaError#constants),
// reused here so hls.js give-ups report through the same failure shape as
// native <audio> errors. The specific cause (error_type, error_detail,
// status_code) is already on the player_hls_error_total metric emitted by
// hlsErrorHandler, which is what Grafana dashboards/alerts should read.
const GIVE_UP_ERROR_CODE = 2;
const GIVE_UP_MESSAGE = 'HLS playback stopped retrying after repeated errors';

export interface AlgoliaSource {
  queryID: string;
  index: AlgoliaIndex;
  objectID: string;
}

export interface CurrentlyPlaying {
  id: string;
  type: 'speaker' | 'story' | 'other' | 'series';
  audioType: 'audio' | 'teaser';
  description?: string;
  speakerName?: string;
  storyTitle?: string;
  otherTitle?: string;
  audioUrl: string;
  trackingCategory?: string;
  sourceUrl?: string;
  algoliaSource?: AlgoliaSource;
  orgasms?: Array<number>;
  contentType?: ContentType;
}

const loadAudioWithHls = ({
  current,
  audioUrl,
  dispatch,
}: {
  current: HTMLAudioElement;
  audioUrl: string;
  dispatch: AppDispatch;
}) => {
  if (hls) {
    destroyHls();
  }

  hls = new Hls({
    xhrSetup: (xhr) => {
      xhr.withCredentials = true;
    },
  });

  hls.attachMedia(current);

  hls.on(Hls.Events.MEDIA_ATTACHED, () => {
    hls.loadSource(
      audioUrl.replace(
        'd26gdlnvsiwmur.cloudfront.net',
        'speaker-teasers.femtasy.com',
      ),
    );
  });

  hlsErrorHandlerInstance = hlsErrorHandler(hls, () => {
    gaveUpOnAudioUrl = audioUrl;
    dispatch(
      playbackFailedThunk(GIVE_UP_ERROR_CODE, GIVE_UP_MESSAGE, 'hls_error'),
    );
  });
  hls.on(Hls.Events.ERROR, hlsErrorHandlerInstance);
};

const updateCurrentlyPlayingAudio = ({
  current,
  currentlyPlaying,
  dispatch,
}: {
  current: HTMLAudioElement;
  currentlyPlaying: CurrentlyPlaying;
  dispatch: AppDispatch;
}) => {
  const { audioUrl } = currentlyPlaying;

  if (!audioUrl || audioUrl === gaveUpOnAudioUrl) return;

  gaveUpOnAudioUrl = undefined;

  if (Hls.isSupported()) {
    loadAudioWithHls({ current, audioUrl, dispatch });
  } else if (current.canPlayType('application/vnd.apple.mpegurl')) {
    current.src = audioUrl;
  }
};

export const handleCurrentlyPlaying = ({
  current,
  currentlyPlaying,
  dispatch,
}: {
  current: HTMLAudioElement;
  currentlyPlaying: CurrentlyPlaying | undefined;
  dispatch: AppDispatch;
}) => {
  if (currentlyPlaying) {
    updateCurrentlyPlayingAudio({ current, currentlyPlaying, dispatch });
  } else if (hls) {
    gaveUpOnAudioUrl = undefined;
    destroyHls();
  } else if (current) {
    current.removeAttribute('src');
  }
};
